<app-auth-layout [heading]="sideHeading" [bullets]="sideBullets" pill="Email verification">

  <!-- Done state: the company was provisioned inside the verify call -->
  <div class="auth-state" *ngIf="isSuccessState">
    <div class="auth-state__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24"><path d="m5 12 5 5L20 7"></path></svg>
    </div>
    <h1>Your trial is ready</h1>
    <p class="auth__lead">{{ successMessage }}</p>

    <ol class="next-steps">
      <li>
        <strong>Check your inbox</strong> for the {{ appName }} welcome email. It contains your
        <strong>company code</strong> and first setup steps.
      </li>
      <li>
        <strong>Sign in</strong> at <a class="auth__link" [href]="loginUrl" rel="noopener">app.shiftclockai.com</a>
        with your company code, this email address and the password you chose.
      </li>
    </ol>

    <a class="btn-auth" [href]="loginUrl" rel="noopener">
      <span>Go to {{ appName }} sign in</span>
      <i class="fa fa-arrow-right" aria-hidden="true"></i>
    </a>
  </div>

  <!-- Verification form -->
  <ng-container *ngIf="!isSuccessState">
    <h1>Check your inbox</h1>
    <p class="auth__lead">
      Enter the 6-digit code we sent to
      <strong *ngIf="email; else noEmail">{{ email }}</strong>
      <ng-template #noEmail>your email address</ng-template>.
      The code is valid for {{ otpValidityMinutes }} minutes.
    </p>

    <form class="auth__form" [formGroup]="otpForm" (ngSubmit)="onSubmit()" novalidate>
      <div class="field">
        <label for="otp">Verification code</label>
        <input
          id="otp"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="6"
          class="form-control otp-input"
          formControlName="otp"
          placeholder="••••••"
          [attr.disabled]="verifyDisabled ? '' : null"
          [class.is-invalid]="f['otp'].invalid && f['otp'].touched"
        />
        <p class="field-error" *ngIf="f['otp'].invalid && f['otp'].touched">Enter the 6-digit numeric code.</p>
      </div>

      <button type="submit" class="btn-auth" [disabled]="verifyDisabled">
        <span>{{ isLoading ? 'Creating your environment…' : 'Verify and create my company' }}</span>
        <i class="fa" [class.fa-arrow-right]="!isLoading" [class.fa-spinner]="isLoading" [class.fa-spin]="isLoading" aria-hidden="true"></i>
      </button>

      <div class="auth-success" role="status" *ngIf="isLoading">
        <i class="fa fa-clock-o" aria-hidden="true"></i>
        <span>Setting up your database and admin account. This can take up to a minute, please keep this page open.</span>
      </div>

      <div class="auth-success" role="status" *ngIf="infoMessage && !isLoading">
        <i class="fa fa-check-circle" aria-hidden="true"></i>
        <span>{{ infoMessage }}</span>
      </div>

      <div class="auth-error" role="alert" *ngIf="failure && !isLoading" [ngSwitch]="failure.outcome">
        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
        <span>
          <ng-container *ngSwitchCase="'retry'">
            That code isn't right.
            <ng-container *ngIf="attemptsLeft > 0">You have {{ attemptsLeft }} {{ attemptsLeft === 1 ? 'attempt' : 'attempts' }} left.</ng-container>
            <ng-container *ngIf="attemptsLeft === 0">Too many incorrect attempts. Request a new code to continue.</ng-container>
          </ng-container>

          <ng-container *ngSwitchCase="'resend'">
            {{ failure.message }}
            <ng-container *ngIf="canResend">Request a new code below.</ng-container>
          </ng-container>

          <ng-container *ngSwitchCase="'login'">
            This email already has a {{ appName }} company.
            <a class="auth__link" [href]="loginUrl" rel="noopener">Sign in at app.shiftclockai.com</a> instead.
          </ng-container>

          <ng-container *ngSwitchCase="'rename'">
            {{ failure.message }}. A company with the same initials already exists, so add a distinguishing word to your company name.
            <a class="auth__link" (click)="editRegistration('company')">Change company name</a>
          </ng-container>

          <ng-container *ngSwitchCase="'timeout'">
            {{ failure.message }}
          </ng-container>

          <ng-container *ngSwitchDefault>
            We hit a problem creating your environment and our team has been notified. Please don't try again with this email;
            <a class="auth__link" [href]="websiteUrl" rel="noopener">contact {{ appName }} support</a> and we'll finish the setup for you.
          </ng-container>
        </span>
      </div>
    </form>

    <p class="auth__alt" *ngIf="!isTerminal">
      Didn't get the code?
      <ng-container *ngIf="resendCooldown > 0; else resendLink">Resend available in {{ resendCooldown }}s</ng-container>
      <ng-template #resendLink>
        <a class="auth__link" (click)="resendOTP()" *ngIf="canResend">{{ isResending ? 'Sending…' : 'Resend code' }}</a>
        <span *ngIf="!canResend && !isResending">Your details are no longer on this device.</span>
      </ng-template>
    </p>
    <p class="auth__alt">
      Wrong email or details?
      <a class="auth__link" (click)="editRegistration('details')">Edit registration</a>
      <ng-container *ngIf="isTerminal"> · <a class="auth__link" (click)="startOver()">Start over</a></ng-container>
    </p>
  </ng-container>
</app-auth-layout>
